import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Wallet, X, ArrowUpRight, ArrowDownLeft, Plus, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const BORDER = "#E7E5E2";

const fmtBRL = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const TOPUP_SUGGESTIONS = [50, 100, 200, 500];

/**
 * <WalletChip> — chip discreto no header + drawer nativo.
 * Regra (Seção 4): mostra "Créditos • R$ XXX,XX" mesmo com saldo 0.
 */
export default function WalletChip() {
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/billing/wallet?limit=15")
    .then(r => { setBalance(r.data?.balance ?? 0); setHistory(r.data?.history || []); })
    .catch(() => setBalance(0));

  useEffect(() => { load(); }, []);

  return (
    <>
      <button
        onClick={() => { setOpen(true); load(); }}
        data-testid="wallet-chip"
        className="hidden md:inline-flex items-center gap-2 h-9 pl-2.5 pr-3 rounded-full border transition hover:shadow-sm bg-white"
        style={{ borderColor: BORDER }}
        title="Carteira Hermes"
      >
        <span className="w-6 h-6 rounded-full inline-flex items-center justify-center" style={{ background: `${GOLD}22`, color: GOLD }}>
          <Wallet className="w-3.5 h-3.5"/>
        </span>
        <span className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Créditos</span>
        <span className="text-[13px] font-serif font-medium text-[#132238] tabular-nums">
          {balance == null ? "—" : fmtBRL(balance)}
        </span>
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <Drawer
          balance={balance ?? 0}
          history={history}
          busy={busy}
          onClose={() => setOpen(false)}
          onReload={load}
          setBusy={setBusy}
        />,
        document.body
      )}
    </>
  );
}

function Drawer({ balance, history, busy, onClose, onReload, setBusy }) {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const requestTopup = async (method) => {
    const value = Number(custom || amount);
    if (!value || value <= 0) return toast.error("Informe um valor válido");
    setBusy(true);
    try {
      const r = await api.post("/billing/wallet/topup", { amount: value, method });
      if (r.data?.status === "AWAITING_PAYMENT" && r.data?.checkout_url) {
        toast.success("Redirecionando para o pagamento…");
        // abre em nova aba para não perder o drawer
        window.open(r.data.checkout_url, "_blank", "noopener,noreferrer");
        // polling de 8s para confirmar crédito ao voltar
        const linkId = r.data.payment_link_id;
        let attempts = 0;
        const poll = setInterval(async () => {
          attempts += 1;
          try {
            const st = await api.get(`/billing/wallet/topup/status/${linkId}`);
            if (st.data?.payment_status === "paid") {
              clearInterval(poll);
              toast.success("Créditos adicionados à sua carteira!");
              onReload();
            }
          } catch { /* ignore */ }
          if (attempts >= 45) clearInterval(poll); // ~6min
        }, 8000);
      } else {
        toast.success("Créditos adicionados");
        onReload();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na recarga");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" style={{ zIndex: 2147483000 }} onClick={onClose}/>
      <aside
        className="fixed top-0 right-0 h-screen w-[420px] max-w-[95vw] bg-white shadow-2xl flex flex-col"
        style={{ zIndex: 2147483001 }}
        data-testid="wallet-drawer"
      >
        <div className="px-6 py-5 border-b flex items-start justify-between" style={{ borderColor: BORDER }}>
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>Hermes</div>
            <div className="font-serif text-2xl text-[#132238] mt-0.5">Carteira Hermes</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center" data-testid="wallet-close">
            <X className="w-4 h-4 text-slate-500"/>
          </button>
        </div>

        {/* Saldo */}
        <div className="px-6 py-6 border-b" style={{ borderColor: BORDER }}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Saldo disponível</div>
          <div className="font-serif text-4xl leading-none text-[#132238] tabular-nums mt-2" data-testid="wallet-balance">
            {fmtBRL(balance)}
          </div>
          {balance === 0 && (
            <div className="mt-3 text-[12px] text-slate-500">Você também pode pagar cada serviço individualmente.</div>
          )}
        </div>

        {/* Recarga */}
        <div className="px-6 py-5 border-b" style={{ borderColor: BORDER }}>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Adicionar créditos</div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {TOPUP_SUGGESTIONS.map(v => (
              <button key={v} onClick={() => { setAmount(v); setCustom(""); }}
                data-testid={`wallet-amount-${v}`}
                className={`h-10 rounded-lg border text-[13px] font-semibold transition ${amount===v && !custom ? "text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                style={{ borderColor: amount===v && !custom ? NAVY : BORDER, background: amount===v && !custom ? NAVY : undefined }}>
                R$ {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="number" min={1} step={10}
              value={custom} onChange={(e) => setCustom(e.target.value)}
              placeholder="Outro valor"
              data-testid="wallet-custom-amount"
              className="flex-1 h-10 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              style={{ borderColor: BORDER }}
            />
            <span className="text-[12px] text-slate-500">BRL</span>
          </div>
          <div className="flex flex-col gap-2">
            <button
              disabled={busy}
              onClick={() => requestTopup("pix")}
              data-testid="wallet-topup-pix"
              className="w-full h-11 rounded-lg text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ background: NAVY }}
            >
              <Plus className="w-4 h-4"/> Pagar com Pix
            </button>
            <button
              disabled={busy}
              onClick={() => requestTopup("card")}
              data-testid="wallet-topup-card"
              className="w-full h-11 rounded-lg border text-[13px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-60"
              style={{ borderColor: BORDER, color: NAVY }}
            >
              Cartão de crédito
            </button>
          </div>
          <div className="mt-2 text-[10.5px] text-slate-400 leading-snug">
            Pagamento processado com segurança pelo Stripe. Crédito é liberado assim que a confirmação chega.
          </div>
        </div>

        {/* Histórico */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 pt-5 pb-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold sticky top-0 bg-white">
            Extrato recente
          </div>
          {history.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-slate-400" data-testid="wallet-empty">
              Nenhuma movimentação ainda.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: BORDER }}>
              {history.map(h => (
                <li key={h.id} className="px-6 py-3 flex items-center gap-3" data-testid={`wallet-entry-${h.id}`}>
                  <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 ${
                    h.direction === "credit" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                  }`}>
                    {h.direction === "credit" ? <ArrowDownLeft className="w-4 h-4"/> : <ArrowUpRight className="w-4 h-4"/>}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[#132238] font-medium truncate">{h.reason || h.type}</div>
                    <div className="text-[11px] text-slate-500">{fmtDT(h.timestamp)}</div>
                  </div>
                  <div className={`text-[13px] font-serif font-semibold tabular-nums ${h.amount >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {h.amount >= 0 ? "+" : ""}{fmtBRL(h.amount)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="px-6 py-4 border-t text-[11px] text-slate-500 flex items-center justify-between" style={{ borderColor: BORDER }}>
          <span>Créditos Hermes · saldo pré-pago</span>
          <a href="/plans" className="inline-flex items-center gap-1 hover:text-[#132238]" data-testid="wallet-plans-link">
            Ver planos <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
      </aside>
    </>
  );
}
